import { unwrap, type Schemas } from '@swarm/api-client';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { api } from '../../lib/api';

export type Sale = Schemas['SaleDto'];
export type SaleListItem = Schemas['SaleListItemDto'];
export type SaleStatus = Schemas['SaleStatusDto'];
export type SaveSaleRequest = Schemas['SaveSaleRequest'];
export type CreateSaleRequest = Schemas['CreateSaleRequest'];
export type SaleTarget = Schemas['SaleTargetDto'];
export type SaleHandover = Schemas['SaleHandoverDto'];
export type PaymentMethod = Schemas['PaymentMethodDto'];
export type PriceListItem = Schemas['PriceListItemDto'];
export type Invoice = Schemas['InvoiceDto'];
export type InvoiceListItem = Schemas['InvoiceListItemDto'];
export type InvoiceStatus = Schemas['InvoiceStatusDto'];
export type MarketDay = Schemas['MarketDayDto'];
export type MarketDayListItem = Schemas['MarketDayListItemDto'];
export type AssortmentItem = Schemas['AssortmentItemDto'];
export type Market = Schemas['MarketDto'];
export type MarketListItem = Schemas['MarketListItemDto'];
export type Schedule = Schemas['ScheduleDto'];
export type SaveMarketRequest = Schemas['SaveMarketRequest'];
export type SalesSettings = Schemas['SalesSettingsDto'];
export type Referrer = Schemas['ReferrerDto'];

export interface SaleFilter {
  customer_id?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
  status?: SaleStatus | undefined;
  paid?: boolean | undefined;
  /** "Vermittelt von" (#26). */
  referred_by_id?: string | undefined;
  /** Column sort on the server (#188): `date`, `-total`, … */
  sort?: string | undefined;
}

const keys = {
  all: ['sales'] as const,
  prices: ['sales', 'prices'] as const,
  sales: (f: SaleFilter) => ['sales', 'list', f] as const,
  sale: (id: string) => ['sales', 'sale', id] as const,
  invoices: (f: object) => ['sales', 'invoices', f] as const,
  invoice: (id: string) => ['sales', 'invoice', id] as const,
  marketDays: ['sales', 'market-days'] as const,
  marketDay: (id: string) => ['sales', 'market-day', id] as const,
  markets: ['sales', 'markets'] as const,
  market: (id: string) => ['sales', 'market', id] as const,
  settings: ['sales', 'settings'] as const,
  referrers: ['sales', 'referrers'] as const,
  summary: (member: string) => ['sales', 'summary', member] as const,
};

/** Sales change stock (after the outbox ran), customers and invoices. */
function invalidate(client: QueryClient) {
  const all = () =>
    Promise.all(
      [keys.all, ['crm'], ['harvest']].map((queryKey) => client.invalidateQueries({ queryKey })),
    );
  // Stock is booked asynchronously (outbox): refresh again shortly after.
  setTimeout(() => void all(), 800);
  return all();
}

const ifMatch = (version?: number): Record<string, string> =>
  version === undefined ? {} : { 'If-Match': `"${version}"` };

// --- Prices -----------------------------------------------------------------------

export function usePrices(enabled = true) {
  return useQuery({
    queryKey: keys.prices,
    queryFn: async () => unwrap(await api.GET('/prices')),
    enabled,
  });
}

export function useSetPrice() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      article,
      price_cents,
      valid_from,
    }: {
      article: string;
      price_cents: number;
      valid_from?: string | undefined;
    }) =>
      unwrap(
        await api.PUT('/prices/{article_id}', {
          params: { path: { article_id: article } },
          body: { price_cents, valid_from: valid_from ?? null },
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}

export function useRemovePrice() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ article, valid_from }: { article: string; valid_from: string }) =>
      unwrap(
        await api.DELETE('/prices/{article_id}/{valid_from}', {
          params: { path: { article_id: article, valid_from } },
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}

// --- Sales ------------------------------------------------------------------------

export function useSales(filter: SaleFilter = {}, enabled = true) {
  return useInfiniteQuery({
    queryKey: keys.sales(filter),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.GET('/sales', {
          params: {
            query: {
              ...(filter.customer_id ? { customer_id: filter.customer_id } : {}),
              ...(filter.from ? { from: filter.from } : {}),
              ...(filter.to ? { to: filter.to } : {}),
              ...(filter.status ? { status: filter.status } : {}),
              ...(filter.paid !== undefined ? { paid: filter.paid } : {}),
              ...(filter.referred_by_id ? { referred_by_id: filter.referred_by_id } : {}),
              ...(filter.sort ? { sort: filter.sort } : {}),
              ...(pageParam ? { cursor: pageParam } : {}),
            },
          },
        }),
      ),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled,
  });
}

export function useSale(id: string, enabled = true) {
  return useQuery({
    queryKey: keys.sale(id),
    queryFn: async () =>
      unwrap(await api.GET('/sales/{sale_id}', { params: { path: { sale_id: id } } })),
    enabled,
  });
}

export function useCreateSale() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateSaleRequest) => unwrap(await api.POST('/sales', { body })),
    onSuccess: () => invalidate(client),
  });
}

export function useUpdateSale() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      body,
      version,
    }: {
      id: string;
      body: SaveSaleRequest;
      version: number;
    }) =>
      unwrap(
        await api.PUT('/sales/{sale_id}', {
          params: { path: { sale_id: id } },
          body,
          headers: ifMatch(version),
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}

/** Current members who can be chosen as "Vermittelt von" (#26). */
export function useReferrers(enabled = true) {
  return useQuery({
    queryKey: keys.referrers,
    queryFn: async () => unwrap(await api.GET('/sales/referrers')),
    enabled,
    staleTime: 60_000,
  });
}

/** Delivered sales of a member, all time and this year (#26). */
export function useSalesSummary(member: string, enabled = true) {
  return useQuery({
    queryKey: keys.summary(member),
    queryFn: async () =>
      unwrap(await api.GET('/sales/summary', { params: { query: { referred_by_id: member } } })),
    enabled: enabled && member !== '',
  });
}

/** Changes "Vermittelt von", also after delivery (#26). */
export function useChangeReferrer() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, member }: { id: string; member: string }) =>
      unwrap(
        await api.PUT('/sales/{sale_id}/referrer', {
          params: { path: { sale_id: id } },
          body: { referred_by_id: member },
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}

export type SaleAction =
  | { action: 'order' }
  | { action: 'deliver'; on?: string | undefined }
  | { action: 'cancel'; confirm_return: boolean }
  | { action: 'pay'; method: PaymentMethod; on?: string | undefined }
  | { action: 'unpay' };

export function useSaleAction() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...a }: SaleAction & { id: string }) => {
      const path = { params: { path: { sale_id: id } } };
      switch (a.action) {
        case 'order':
          return unwrap(await api.POST('/sales/{sale_id}/order', path));
        case 'deliver':
          return unwrap(
            await api.POST('/sales/{sale_id}/deliver', {
              ...path,
              body: { delivered_on: a.on ?? null },
            }),
          );
        case 'cancel':
          return unwrap(
            await api.POST('/sales/{sale_id}/cancel', {
              ...path,
              body: { confirm_return: a.confirm_return },
            }),
          );
        case 'pay':
          return unwrap(
            await api.POST('/sales/{sale_id}/payment', {
              ...path,
              body: { method: a.method, paid_on: a.on ?? null },
            }),
          );
        case 'unpay':
          return unwrap(await api.DELETE('/sales/{sale_id}/payment', path));
      }
    },
    onSuccess: () => invalidate(client),
  });
}

// --- Invoices ---------------------------------------------------------------------

export function useInvoices(
  filter: { customer_id?: string; status?: InvoiceStatus; overdue?: boolean } = {},
  enabled = true,
) {
  return useQuery({
    queryKey: keys.invoices(filter),
    queryFn: async () =>
      unwrap(
        await api.GET('/invoices', {
          params: {
            query: {
              ...(filter.customer_id ? { customer_id: filter.customer_id } : {}),
              ...(filter.status ? { status: filter.status } : {}),
              overdue: filter.overdue ?? false,
            },
          },
        }),
      ),
    enabled,
  });
}

export function useInvoice(id: string) {
  return useQuery({
    queryKey: keys.invoice(id),
    queryFn: async () =>
      unwrap(await api.GET('/invoices/{invoice_id}', { params: { path: { invoice_id: id } } })),
  });
}

export function useCreateInvoice() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      sale_id: string;
      note?: string | null;
      customer_id?: string | null;
      recipient?: { name: string; lines: string[]; email?: string | null } | null;
    }) => unwrap(await api.POST('/invoices', { body: { note: null, ...body } })),
    onSuccess: () => invalidate(client),
  });
}

export type InvoiceAction =
  | { action: 'finalize' }
  | { action: 'cancel' }
  | { action: 'delete' }
  | { action: 'note'; note: string | null }
  | {
      action: 'recipient';
      recipient: { name: string; lines: string[]; email: string | null } | null;
    }
  | { action: 'send'; to?: string | undefined }
  | { action: 'pay'; method: PaymentMethod; on?: string | undefined };

export function useInvoiceAction() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...a }: InvoiceAction & { id: string }) => {
      const path = { params: { path: { invoice_id: id } } };
      switch (a.action) {
        case 'finalize':
          return unwrap(await api.POST('/invoices/{invoice_id}/finalize', path));
        case 'cancel':
          return unwrap(await api.POST('/invoices/{invoice_id}/cancel', path));
        case 'delete':
          return unwrap(await api.DELETE('/invoices/{invoice_id}', path));
        case 'note':
          return unwrap(
            await api.PUT('/invoices/{invoice_id}', { ...path, body: { note: a.note } }),
          );
        case 'recipient':
          return unwrap(
            await api.PUT('/invoices/{invoice_id}/recipient', {
              ...path,
              body: { recipient: a.recipient },
            }),
          );
        case 'send':
          return unwrap(
            await api.POST('/invoices/{invoice_id}/send', { ...path, body: { to: a.to ?? null } }),
          );
        case 'pay':
          return unwrap(
            await api.POST('/invoices/{invoice_id}/payment', {
              ...path,
              body: { method: a.method, paid_on: a.on ?? null },
            }),
          );
      }
    },
    onSuccess: () => invalidate(client),
  });
}

/** PDF of an invoice, served by the API with the session cookie. */
export function invoicePdfUrl(id: string, version: number): string {
  return `/api/v1/invoices/${id}/pdf?v=${version}`;
}

// --- Market days ------------------------------------------------------------------

export function useMarketDays(enabled = true) {
  return useQuery({
    queryKey: keys.marketDays,
    queryFn: async () => unwrap(await api.GET('/market-days')),
    enabled,
  });
}

/** My own open market days of the next seven days (MKT-016). */
export function useMyMarketDays(enabled = true) {
  return useQuery({
    queryKey: [...keys.marketDays, 'mine'] as const,
    queryFn: async () => unwrap(await api.GET('/market-days/mine')),
    enabled,
  });
}

export function useMarketDay(id: string) {
  return useQuery({
    queryKey: keys.marketDay(id),
    queryFn: async () =>
      unwrap(
        await api.GET('/market-days/{market_day_id}', {
          params: { path: { market_day_id: id } },
        }),
      ),
  });
}

export function useOpenMarketDay() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: Schemas['SaveMarketDayRequest']) =>
      unwrap(await api.POST('/market-days', { body })),
    onSuccess: () => invalidate(client),
  });
}

// --- Markets (MKT-003) -------------------------------------------------------------

export function useMarkets() {
  return useQuery({
    queryKey: keys.markets,
    queryFn: async () => unwrap(await api.GET('/markets')),
  });
}

export function useMarket(id: string) {
  return useQuery({
    queryKey: keys.market(id),
    queryFn: async () =>
      unwrap(await api.GET('/markets/{market_id}', { params: { path: { market_id: id } } })),
  });
}

export function useCreateMarket() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: SaveMarketRequest) => unwrap(await api.POST('/markets', { body })),
    onSuccess: () => invalidate(client),
  });
}

export function useUpdateMarket(id: string, version?: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: SaveMarketRequest) =>
      unwrap(
        await api.PUT('/markets/{market_id}', {
          params: { path: { market_id: id } },
          body,
          headers: ifMatch(version),
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}

/** The dates a schedule yields; `null` while the schedule is invalid. */
export function useScheduleDates(schedule: Schedule) {
  return useQuery({
    queryKey: ['sales', 'market-preview', schedule] as const,
    queryFn: async () => unwrap(await api.POST('/markets/preview', { body: schedule })).dates,
    retry: false,
    placeholderData: (previous) => previous,
  });
}

/** Sets the standard assortment new days of a market start with. */
export function useSetMarketAssortment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      market,
      items,
    }: {
      market: string;
      items: Schemas['AssortmentItemRequest'][];
    }) =>
      unwrap(
        await api.PUT('/markets/{market_id}/assortment', {
          params: { path: { market_id: market } },
          body: { items },
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}

/** Sets the standard sellers new days of a market start with (MKT-015). */
export function useSetMarketSellers() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ market, sellerIds }: { market: string; sellerIds: string[] }) =>
      unwrap(
        await api.PUT('/markets/{market_id}/sellers', {
          params: { path: { market_id: market } },
          body: { seller_ids: sellerIds },
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}

/** Sets the sellers of an open market day (MKT-015). */
export function useSetDaySellers() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ day, sellerIds }: { day: string; sellerIds: string[] }) =>
      unwrap(
        await api.PUT('/market-days/{market_day_id}/sellers', {
          params: { path: { market_day_id: day } },
          body: { seller_ids: sellerIds },
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}

export function useCancelMarketDay() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (day: string) =>
      unwrap(
        await api.POST('/market-days/{market_day_id}/cancel', {
          params: { path: { market_day_id: day } },
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}

/** Replaces the assortment of an open market day (MKT-008). */
export function useSetAssortment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      day,
      items,
    }: {
      day: string;
      items: Schemas['AssortmentItemRequest'][];
    }) =>
      unwrap(
        await api.PUT('/market-days/{market_day_id}/assortment', {
          params: { path: { market_day_id: day } },
          body: { items },
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}

/**
 * The assortment of the last earlier market day that has one, preferring the
 * same place (MKT-008); empty if there is none.
 */
export async function lastAssortment(
  day: Pick<MarketDay, 'id' | 'held_on' | 'place'>,
  days: MarketDayListItem[],
): Promise<AssortmentItem[]> {
  const earlier = days.filter((d) => d.id !== day.id && d.held_on <= day.held_on);
  const ordered = [
    ...earlier.filter((d) => d.place === day.place),
    ...earlier.filter((d) => d.place !== day.place),
  ].slice(0, 8);
  for (const d of ordered) {
    const found = unwrap(
      await api.GET('/market-days/{market_day_id}', { params: { path: { market_day_id: d.id } } }),
    );
    if (found.assortment.length > 0) return found.assortment;
  }
  return [];
}

export function useMarketSale() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ day, body }: { day: string; body: Schemas['MarketSaleRequest'] }) =>
      unwrap(
        await api.POST('/market-days/{market_day_id}/sales', {
          params: { path: { market_day_id: day } },
          body,
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}

export function useCollectiveEntry() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ day, body }: { day: string; body: Schemas['CollectiveRequest'] }) =>
      unwrap(
        await api.POST('/market-days/{market_day_id}/collective', {
          params: { path: { market_day_id: day } },
          body,
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}

export function useCloseMarketDay() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (day: string) =>
      unwrap(
        await api.POST('/market-days/{market_day_id}/close', {
          params: { path: { market_day_id: day } },
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}

// --- Settings ---------------------------------------------------------------------

export function useSalesSettings(enabled = true) {
  return useQuery({
    queryKey: keys.settings,
    queryFn: async () => unwrap(await api.GET('/sales-settings')),
    enabled,
  });
}

export function useSaveSalesSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      body,
      version,
    }: {
      body: Schemas['SaveSalesSettingsRequest'];
      version: number;
    }) =>
      unwrap(
        await api.PUT('/sales-settings', {
          body,
          headers: ifMatch(version),
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}
